/**
 * Shared notification utility functions
 * Single source of truth for notification handling across web and native
 */

import i18n from 'i18next';

export interface NotificationPayload {
	_id?: string;
	jobId?: string;
	message?: string;
	body?: string;
	title?: string;
	timestamp?: number | string;
	type?: string;
	operationType?: string;
	noticeType?: string;
	ns?: {
		coll: string;
	};
	postedBy?: any;
	page?: string;
	screen?: string;
	[key: string]: any;
}

export interface MappedNotification extends NotificationPayload {
	_id: string;
	message: string;
	timestamp: string;
	type: string;
	noticeType: 'notification';
	title?: string;
	body?: string;
	operationType?: string;
	ns?: {
		coll: string;
	};
}

/**
 * Page badge mapping for MongoDB change stream events
 */
export const PAGE_BADGE_MAP: Record<string, string> = {
	user_datas: 'data',
	data_items: 'data',
	projects: 'projectmanagement',
	calendars: 'organiser',
};

/**
 * Maps a test event to the standard notification format
 */
export function mapTestEvent(data: any): MappedNotification {
	return {
		...data,
		message: data.message || 'Test notification',
		timestamp: new Date().toISOString(),
		type: 'test_notification',
		operationType: 'test',
		noticeType: 'notification',
		_id: data._id || `test_${Date.now()}`,
		title: data.message || 'Test Event',
		body: data.message || 'Test notification',
		ns: { coll: 'test_events' },
	};
}

/**
 * Maps orchestration decision socket payload to notification center format
 */
export function mapOrchestrationDecisionNotification(notification: any): MappedNotification {
	const summary =
		notification.outputSummary ||
		notification.phases?.find?.((p: any) => p.phase === 'output')?.summary ||
		notification.message ||
		'Orchestration decision recorded';
	return {
		...notification,
		message: summary,
		timestamp: notification.timestamp || new Date().toISOString(),
		type: 'orchestration_decision',
		operationType: 'update',
		noticeType: 'notification',
		_id: notification.decisionId || notification._id || `orch_decision_${Date.now()}`,
		title: notification.title || `Orchestrator · ${notification.layer || 'decision'}`,
		body: summary,
		ns: { coll: 'orchestration_decisions' },
	};
}

/**
 * Maps a proactive/agent suggestion socket payload to notification center format
 */
export function mapAgentSuggestionNotification(notification: any): MappedNotification {
	return {
		...notification,
		message: notification.message || notification.title || 'New agent suggestion',
		timestamp: notification.timestamp || new Date().toISOString(),
		type: notification.type || 'agent_suggestion',
		operationType: 'update',
		noticeType: 'notification',
		_id: notification.suggestionId || notification.metadata?.suggestionId || `agent_suggestion_${Date.now()}`,
		title: notification.title || 'Agent suggestion',
		body: notification.message || notification.title,
		ns: { coll: 'agent_suggestions' },
	};
}

export type CrmMailMatch = {
	contentId: string;
	dataId: string;
	title?: string;
	datasetTitle?: string;
	email?: string;
};

export function getCrmMatchesFromNotice(item: Record<string, unknown> | null | undefined): CrmMailMatch[] {
	const matches = item?.crmMatches;
	if (!Array.isArray(matches)) return [];
	return matches.filter((match: CrmMailMatch) => match?.contentId && match?.dataId);
}

/**
 * Maps a Gmail unread-count increase to the floating notice format.
 */
export function mapNewMailNotification(
	unreadDelta: number,
	totalUnread: number,
	extras?: {
		fromEmail?: string;
		fromName?: string;
		crmMatches?: CrmMailMatch[];
	}
): MappedNotification {
	const count = Math.max(1, Number(unreadDelta) || 1);
	const message =
		count === 1
			? i18n.t('mailNewInboxBodyOne')
			: i18n.t('mailNewInboxBodyOther', { count });
	const crmMatches = Array.isArray(extras?.crmMatches) ? extras.crmMatches : [];
	return {
		_id: `mail_inbox_${Date.now()}`,
		type: 'new_mail',
		noticeType: 'notification',
		operationType: 'insert',
		title: i18n.t('mailNewInboxTitle'),
		message,
		body: message,
		timestamp: new Date().toISOString(),
		stack: 'Mail',
		screen: 'Main',
		page: 'Mail',
		unreadDelta: count,
		unreadCount: totalUnread,
		fromEmail: extras?.fromEmail,
		fromName: extras?.fromName,
		crmMatches,
		ns: { coll: 'gmail_inbox' },
	};
}

/**
 * Maps a job notification to the standard notification format
 */
export function mapJobNotification(notification: any): MappedNotification {
	const operationType = notification.type?.includes('complete')
		? 'complete'
		: notification.type?.includes('failed')
			? 'failed'
			: 'update';

	return {
		...notification,
		message: notification.message || notification.title,
		timestamp: notification.timestamp || new Date().toISOString(),
		type: notification.type || 'job_notification',
		operationType,
		noticeType: 'notification',
		_id: notification.jobId || notification._id || `job_${Date.now()}`,
		title: notification.title,
		body: notification.message || notification.title,
		ns: { coll: 'job_notifications' },
	};
}

const LIVE_FOLDER_ROOTS = new Set(['liveSource', 'dataSchema', 'updatedAt', '__v']);

/**
 * Live dataset polls write rows constantly. Those updates are refreshed through
 * `live_dataset_updated` and must not become a bell notification.
 */
export function isLiveDatasetSyncChange(event: any): boolean {
	const doc = event?.fullDocument || {};
	if (doc?.syncMetadata?.integrationType === 'live_source') return true;
	const fields = Object.keys(event?.updateDescription?.updatedFields || {});
	if (!doc?.liveSource || fields.length === 0) return false;
	return fields.every((field) => LIVE_FOLDER_ROOTS.has(String(field).split('.')[0]));
}

/**
 * Maps a data notification (MongoDB change stream) to the standard format
 */
export function mapDataNotification(event: any): MappedNotification {
	return {
		...event,
		message: event.message || event.title || 'New data update',
		timestamp: event.timestamp || new Date().toISOString(),
		type: event.type || 'data_notification',
		noticeType: 'notification',
		_id: event._id || event.documentKey?._id || `data_${Date.now()}`,
		title: event.title,
		body: event.body || event.message,
		ns: event.ns,
	};
}

/**
 * Gets the page badge for a notification based on its collection
 */
export function getPageBadgeForNotification(
	notification: NotificationPayload
): string | null {
	if (notification.page || notification.screen) {
		return (notification.page || notification.screen).toLowerCase();
	}
	const coll = notification.ns?.coll || notification.collection;
	if (coll) {
		return PAGE_BADGE_MAP[coll] || null;
	}
	return null;
}

const ENTITY_BADGE_WINDOW_MS = 1000;
const recentEntityBadges = new Map<string, number>();

function toEntityId(value: unknown): string | null {
	if (value == null || value === '') return null;
	if (typeof value === 'string' || typeof value === 'number') return String(value);
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		if (record.$oid != null) return String(record.$oid);
		if (typeof (record as { toHexString?: () => string }).toHexString === 'function') {
			return String((record as { toHexString: () => string }).toHexString());
		}
		if (record._id != null && record._id !== value) {
			return toEntityId(record._id);
		}
	}
	return null;
}

export type EntityBadgeTarget = {
	kind: 'data' | 'projects';
	id: string;
	count: number;
};

/**
 * Dataset or project a change-stream notification belongs to.
 * Data item events count against the parent dataset.
 */
export function getEntityBadgeTarget(
	notification: NotificationPayload
): EntityBadgeTarget | null {
	const coll = String(notification?.ns?.coll || notification?.collection || '').toLowerCase();
	const docId = toEntityId(notification?.documentKey?._id) || toEntityId(notification?.documentKey);
	const dataId = toEntityId(notification?.dataId) || toEntityId(notification?.fullDocument?.dataId);

	if (coll === 'data_items' || coll === 'dataitems' || notification?.type === 'data_bulk') {
		if (!dataId) return null;
		const rawCount = Number(notification?.count);
		const count = notification?.type === 'data_bulk' && rawCount > 0 ? rawCount : 1;
		return { kind: 'data', id: dataId, count };
	}

	if (coll === 'user_datas' || coll === 'datas') {
		const id = dataId || docId;
		if (!id) return null;
		return { kind: 'data', id, count: 1 };
	}

	if (coll === 'projects' || coll === 'project') {
		const id = docId || toEntityId(notification?.projectId) || toEntityId(notification?._id);
		if (!id) return null;
		return { kind: 'projects', id, count: 1 };
	}

	return null;
}

/**
 * Collapses duplicate change-stream deliveries for the same item.
 * Bulk counts always pass so a batched insert is not dropped.
 */
export function claimEntityBadge(kind: string, id: string, count: number): number {
	const safeCount = Number(count) || 0;
	if (!kind || !id || safeCount <= 0) return 0;
	const key = `${kind}:${id}`;
	const now = Date.now();
	const last = recentEntityBadges.get(key) || 0;
	if (safeCount <= 1 && now - last <= ENTITY_BADGE_WINDOW_MS) return 0;
	recentEntityBadges.set(key, now);
	if (recentEntityBadges.size > 500) {
		for (const [entryKey, timestamp] of recentEntityBadges) {
			if (now - timestamp > ENTITY_BADGE_WINDOW_MS * 10) recentEntityBadges.delete(entryKey);
		}
	}
	return safeCount;
}

/**
 * Checks if a notification is a job/test notification (doesn't need page badge)
 */
export function isJobOrTestNotification(
	notification: NotificationPayload
): boolean {
	if (!notification.type) return false;
	return (
		notification.type.includes('huggingface') ||
		notification.type.includes('job') ||
		notification.type.includes('test') ||
		notification.type === 'proactive_ai_suggestion' ||
		notification.type === 'proactive_ai_failed'
	);
}

/**
 * Creates a unique ID for a notification for deduplication purposes
 */
export function getNotificationId(notification: NotificationPayload): string {
	return (
		notification._id ||
		notification.jobId ||
		`${notification.type}_${notification.timestamp}_${notification.message || ''}`
	);
}

/**
 * Stable deduplication key for floating notice display.
 * Handles malformed payloads; returns null only for empty placeholder objects.
 */
export function getNotificationDeduplicationKey(item: any): string | null {
	if (!item || typeof item !== 'object') return null;
	if (Object.keys(item).length === 0) return null;

	const id =
		item._id?.toString?.() ||
		item.notificationId?.toString?.() ||
		item.jobId?.toString?.() ||
		item.timestamp?.toString?.() ||
		null;

	if (id) return id;

	const msg = String(item.message || item.title || '').slice(0, 80);
	const type = String(item.type || 'n');
	const ts = item.timestamp ? String(item.timestamp) : '';
	if (msg || type !== 'n') {
		return `content_${type}_${msg}_${ts}`;
	}

	return `fallback_${Date.now()}`;
}

/**
 * Checks if two notifications are duplicates
 */
export function areNotificationsDuplicate(
	notification1: NotificationPayload,
	notification2: NotificationPayload
): boolean {
	// Check by _id
	if (
		notification1._id &&
		notification2._id &&
		notification1._id === notification2._id
	) {
		return true;
	}

	// Check by jobId
	if (
		notification1.jobId &&
		notification2.jobId &&
		notification1.jobId === notification2.jobId
	) {
		return true;
	}

	// Check by message and timestamp (within 2 seconds)
	if (
		notification1.message &&
		notification2.message &&
		notification1.message === notification2.message &&
		notification1.timestamp &&
		notification2.timestamp
	) {
		const time1 = new Date(notification1.timestamp).getTime() || 0;
		const time2 = new Date(notification2.timestamp).getTime() || 0;
		const timeDiff = Math.abs(time1 - time2);
		if (timeDiff < 2000) {
			return true;
		}
	}

	return false;
}

/**
 * Deduplicates an array of notifications
 */
export function deduplicateNotifications(
	notifications: NotificationPayload[]
): NotificationPayload[] {
	const seen = new Set<string>();
	const result: NotificationPayload[] = [];

	for (const notification of notifications) {
		const id = getNotificationId(notification);
		if (!seen.has(id)) {
			seen.add(id);
			result.push(notification);
		}
	}

	return result;
}

/**
 * Processes a notification and ensures it has all required fields
 * Returns a new object to avoid mutating the input
 *
 * Important: do NOT invent a bare document-name title when enrichment fields exist.
 * NoticeItem/NotificationItem use getContextualMessage, which builds richer text from
 * updateDescription / fullDocument / message — inventing title used to short-circuit that.
 */
export function normalizeNotification(
	notification: NotificationPayload
): MappedNotification {
	// Create a copy to avoid mutating the original
	const normalized = { ...notification };

	// Best-effort derive a human friendly label from fullDocument fields
	const deriveTitleFromDocument = (): string | undefined => {
		const doc = (normalized as any).fullDocument;
		if (!doc) return undefined;
		const candidate =
			doc.dataTitle ??
			doc.title ??
			doc.name ??
			doc.company_name ??
			doc.email;
		if (typeof candidate !== 'string') return undefined;
		const trimmed = candidate.trim();
		return trimmed.length > 0 ? trimmed : undefined;
	};

	const hasFieldEnrichment =
		!!(normalized as any).updateDescription?.updatedFields &&
		Object.keys((normalized as any).updateDescription.updatedFields).length > 0;

	// Ensure noticeType is set
	if (!normalized.noticeType) {
		normalized.noticeType = 'notification';
	}

	// Ensure _id exists
	if (!normalized._id) {
		normalized._id = getNotificationId(normalized);
	}

	// Ensure timestamp is a string
	if (normalized.timestamp && typeof normalized.timestamp === 'number') {
		normalized.timestamp = new Date(normalized.timestamp).toISOString();
	} else if (!normalized.timestamp) {
		normalized.timestamp = new Date().toISOString();
	}

	// Ensure message exists (required by MappedNotification)
	if (!normalized.message) {
		normalized.message = normalized.title || normalized.body || 'Notification';
	}

	// Preserve original title when present. Only invent a title when there is no
	// field-level enrichment to drive getContextualMessage.
	if (!normalized.title && !hasFieldEnrichment) {
		normalized.title = deriveTitleFromDocument() || normalized.message;
	} else if (!normalized.title && hasFieldEnrichment) {
		// Keep a soft label for grouping/logs without blocking enrichment in the UI
		normalized.dataTitle =
			(normalized as any).dataTitle || deriveTitleFromDocument();
	}

	if (!normalized.body) {
		normalized.body = normalized.message;
	}

	// Ensure type exists (required by MappedNotification)
	if (!normalized.type) {
		normalized.type = normalized.operationType 
			? `data_${normalized.operationType}` 
			: 'notification';
	}

	return normalized as MappedNotification;
}

/**
 * Creates a deduplication tracker that prevents processing the same notification multiple times
 */
export class NotificationDeduplicationTracker {
	private processedIds: Set<string> = new Set();
	private maxSize: number;

	constructor(maxSize: number = 100) {
		this.maxSize = maxSize;
	}

	/**
	 * Checks if a notification has already been processed
	 */
	hasBeenProcessed(notification: NotificationPayload): boolean {
		const id = getNotificationId(notification);
		return this.processedIds.has(id);
	}

	/**
	 * Marks a notification as processed
	 */
	markAsProcessed(notification: NotificationPayload): void {
		const id = getNotificationId(notification);
		this.processedIds.add(id);

		// Clean up old IDs if we exceed max size
		if (this.processedIds.size > this.maxSize) {
			const idsArray = Array.from(this.processedIds);
			const toRemove = idsArray.slice(0, idsArray.length - this.maxSize);
			toRemove.forEach((idToRemove) =>
				this.processedIds.delete(idToRemove)
			);
		}
	}

	/**
	 * Clears all processed IDs
	 */
	clear(): void {
		this.processedIds.clear();
	}
}

/**
 * Type for the dispatch function used by DebouncedBadgeUpdater
 */
export type BadgeDispatchFunction = (action: {
	type: string;
	payload: { page: string; count: number; agents?: any[] };
}) => void;

/**
 * Debounced badge updater that batches multiple badge updates to reduce Redux dispatches
 * Accumulates badge counts by page and dispatches them after a debounce delay
 */
export class DebouncedBadgeUpdater {
	private pendingBadges: Map<string, { count: number; agents: any[] }> = new Map();
	private debounceTimer: ReturnType<typeof setTimeout> | null = null;
	private dispatchFn: BadgeDispatchFunction | null = null;
	private debounceDelay: number;
	private actionType: string;

	/**
	 * @param dispatchFn - Redux dispatch function
	 * @param actionType - Redux action type (e.g., 'UPDATED PAGE BADGES')
	 * @param debounceDelay - Delay in milliseconds before dispatching (default: 300ms)
	 */
	constructor(
		dispatchFn: BadgeDispatchFunction,
		actionType: string,
		debounceDelay: number = 300
	) {
		this.dispatchFn = dispatchFn;
		this.actionType = actionType;
		this.debounceDelay = debounceDelay;
	}

	/**
	 * Queues a badge update for a specific page
	 * Multiple calls within the debounce delay will be batched together
	 */
	updateBadge(page: string, count: number = 1, agent?: any): void {
		if (!page || !this.dispatchFn) {
			return;
		}

		// Accumulate badge counts by page
		const current = this.pendingBadges.get(page) || { count: 0, agents: [] };
		if (agent) {
			current.agents.push(agent);
		}
		this.pendingBadges.set(page, { count: current.count + count, agents: current.agents });

		// Clear existing timer
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}

		// Set new timer to dispatch after debounce delay
		this.debounceTimer = setTimeout(() => {
			this.flush();
		}, this.debounceDelay);
	}

	/**
	 * Immediately dispatches all pending badge updates
	 * Useful for cleanup or when you need immediate updates
	 */
	flush(): void {
		if (!this.dispatchFn || this.pendingBadges.size === 0) {
			return;
		}

		// Dispatch each page's accumulated badge count
		for (const [page, data] of this.pendingBadges.entries()) {
			if (data.count !== 0 || data.agents.length > 0) {
				this.dispatchFn({
					type: this.actionType,
					payload: { page, count: data.count, agents: data.agents },
				});
			}
		}

		// Clear pending badges and timer
		this.pendingBadges.clear();
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}
	}

	/**
	 * Clears all pending badge updates without dispatching
	 */
	clear(): void {
		this.pendingBadges.clear();
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}
	}

	/**
	 * Updates the dispatch function (useful if Redux store changes)
	 */
	setDispatch(dispatchFn: BadgeDispatchFunction): void {
		this.dispatchFn = dispatchFn;
	}

	/**
	 * Cleanup method - should be called when the updater is no longer needed
	 */
	destroy(): void {
		this.flush(); // Dispatch any remaining updates before cleanup
		this.clear();
		this.dispatchFn = null;
	}
}









